/**
 * Circular orbits around a point or spherical mass M.
 *
 * Gravity supplies the centripetal force, G M m / r² = m v² / r, so
 *
 *   v = √(G M / r)             circular orbital speed
 *   T = 2π √(r³ / (G M))       period — Kepler's third law, T² = (4π² / G M) r³
 *   v_esc = √(2 G M / r)       escape speed, √2 times the circular speed
 *
 * `r` is the distance from the centre of M, so an orbit's altitude is r − R. SI units.
 */

import { GRAVITATIONAL_CONSTANT } from "@/lib/tool-kit/physics-fields/constants";

export type OrbitKind = "radius" | "altitude" | "speed" | "period";

export type OrbitError =
  | "missing"
  | "non-positive"
  | "radius-needed"
  | "inside-body"
  | "not-finite";

export interface OrbitInputs {
  /** Mass of the central body, kg. */
  mass: number | null;
  /** Radius of the central body, m (needed for altitude and surface values). */
  radius: number | null;
  /** The orbit's described quantity, in SI. */
  kind: OrbitKind;
  value: number | null;
}

export interface OrbitSolution {
  /** Orbital radius from the centre, m. */
  distance: number;
  /** Altitude above the surface, m — null when no body radius was given. */
  altitude: number | null;
  /** Circular orbital speed, m/s. */
  speed: number;
  /** Orbital period, s. */
  period: number;
  /** Escape speed at this distance, m/s. */
  escapeSpeed: number;
  /** Centripetal (gravitational) acceleration at this distance, m/s². */
  acceleration: number;
  /** Specific orbital energy −GM/2r, J/kg. */
  specificEnergy: number;
  /** Circular speed just above the surface, m/s — null without a body radius. */
  surfaceCircularSpeed: number | null;
  /** Escape speed from the surface, m/s — null without a body radius. */
  surfaceEscapeSpeed: number | null;
  /** The body's gravitational parameter GM, m³/s². */
  mu: number;
}

function isPositive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/** The orbital radius implied by whichever quantity was entered. */
function radiusFrom(
  kind: OrbitKind,
  value: number,
  mu: number,
  bodyRadius: number | null
): number {
  switch (kind) {
    case "altitude":
      return (bodyRadius ?? 0) + value;
    case "speed":
      return mu / value ** 2;
    case "period":
      return ((mu * value ** 2) / (4 * Math.PI ** 2)) ** (1 / 3);
    default:
      return value;
  }
}

export function solveOrbit(
  inputs: OrbitInputs
): { ok: true; value: OrbitSolution } | { ok: false; error: OrbitError } {
  const { mass, radius, kind, value } = inputs;
  if (mass === null || value === null) {
    return { ok: false, error: "missing" };
  }
  const valueOk = kind === "altitude" ? value >= 0 : isPositive(value);
  if (
    !(isPositive(mass) && valueOk) ||
    (radius !== null && !isPositive(radius))
  ) {
    return { ok: false, error: "non-positive" };
  }
  if (kind === "altitude" && radius === null) {
    return { ok: false, error: "radius-needed" };
  }
  const mu = GRAVITATIONAL_CONSTANT * mass;
  const distance = radiusFrom(kind, value, mu, radius);
  if (radius !== null && distance < radius * (1 - 1e-12)) {
    return { ok: false, error: "inside-body" };
  }
  const speed = Math.sqrt(mu / distance);
  const solution: OrbitSolution = {
    distance,
    altitude: radius === null ? null : distance - radius,
    speed,
    period: (2 * Math.PI * distance) / speed,
    escapeSpeed: Math.SQRT2 * speed,
    acceleration: mu / distance ** 2,
    specificEnergy: -mu / (2 * distance),
    surfaceCircularSpeed: radius === null ? null : Math.sqrt(mu / radius),
    surfaceEscapeSpeed: radius === null ? null : Math.sqrt((2 * mu) / radius),
    mu,
  };
  const numbers = Object.values(solution).filter(
    (item): item is number => typeof item === "number"
  );
  if (!numbers.every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/** Kepler's third-law ratio T²/r³ = 4π²/(GM), s²/m³. */
export function keplerConstant(mass: number): number {
  return (4 * Math.PI ** 2) / (GRAVITATIONAL_CONSTANT * mass);
}

const YEAR = 365.25 * 86_400;
const DURATION_PARTS = [
  { symbol: "yr", seconds: YEAR },
  { symbol: "d", seconds: 86_400 },
  { symbol: "h", seconds: 3600 },
  { symbol: "min", seconds: 60 },
  { symbol: "s", seconds: 1 },
] as const;

/**
 * A period broken into its three largest units — "1 h 32 min 35 s", "365 d 6 h 12 min".
 * Years appear only from two years up. Unit symbols only, no prose. Under one minute:
 * seconds with one decimal.
 */
export function describeDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "";
  }
  if (seconds < 60) {
    return `${seconds.toFixed(1)} s`;
  }
  const parts = DURATION_PARTS.filter(
    (part) => part.symbol !== "yr" || seconds >= 2 * YEAR
  );
  const first = parts.findIndex((part) => seconds >= part.seconds);
  let left = Math.round(seconds);
  const out: string[] = [];
  for (const part of parts.slice(first, first + 3)) {
    const whole = Math.floor(left / part.seconds);
    out.push(`${whole} ${part.symbol}`);
    left -= whole * part.seconds;
  }
  while (out.length > 1 && out.at(-1)?.startsWith("0 ")) {
    out.pop();
  }
  return out.join(" ");
}
